import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Scale, Plus, Pencil, RefreshCw, Save, X, Check, XCircle, AlertTriangle, Loader2, Download, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { unidadesApi, UnidadeRow } from '../../api/tabelasBasicas'
import { exportToExcel } from '../../utils/exportToExcel'

type ToastType = 'success' | 'error' | 'warning'
interface Toast { id: number; msg: string; type: ToastType }

const UnidadesPage: React.FC = () => {
  const { getEmpresaId } = useAuth()
  const empresaId = getEmpresaId()
  const { t } = useTranslation()

  const [rows, setRows] = useState<UnidadeRow[]>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [editMode, setEditMode] = useState<'none' | 'new' | 'edit'>('none')
  const [search, setSearch] = useState('')
  
  // Estado do formulário com os 7 campos oficiais
  const [form, setForm] = useState<UnidadeRow>({
    cdunid: '',
    nmunid: '',
    status: 'S',
    cdunidnfe: '',
    qtdecasasdec: 0,
    cdunidpai: '',
    fatorunidpai: 0,
  })

  const [saving, setSaving] = useState(false)
  const [toasts, setToasts] = useState<Toast[]>([])
  const toastId = useRef(0)

  const toast = useCallback((msg: string, type: ToastType = 'success') => {
    const id = ++toastId.current
    setToasts(t => [...t, { id, msg, type }])
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500)
  }, [])

  const load = useCallback(async () => {
    if (!empresaId) { toast(t('unidades.noEmpresa'), 'error'); return }
    setLoading(true)
    try {
      const data = await unidadesApi.list(empresaId)
      setRows(data)
    } catch (e: any) {
      toast(e?.response?.data?.detail || t('unidades.loadError'), 'error')
    } finally { setLoading(false) }
  }, [empresaId, toast, t])

  useEffect(() => {
    load()
  }, [load])

  const handleNew = () => {
    setForm({
      cdunid: '',
      nmunid: '',
      status: 'S',
      cdunidnfe: '',
      qtdecasasdec: 0,
      cdunidpai: '',
      fatorunidpai: 1.0,
    })
    setEditMode('new')
    setSelected(null)
  }

  const handleEdit = () => {
    if (!selected) return
    const row = rows.find(r => r.cdunid === selected)
    if (!row) return
    setForm({
      cdunid: row.cdunid,
      nmunid: row.nmunid,
      status: row.status,
      cdunidnfe: row.cdunidnfe || '',
      qtdecasasdec: row.qtdecasasdec,
      cdunidpai: row.cdunidpai || '',
      fatorunidpai: row.fatorunidpai,
    })
    setEditMode('edit')
  }

  const handleCancel = () => {
    setEditMode('none')
  }

  const handleSave = async () => {
    if (!form.cdunid.trim() && editMode === 'new') { toast(t('unidades.codeRequired'), 'error'); return }
    if (!form.nmunid.trim()) { toast(t('unidades.descriptionRequired'), 'error'); return }
    
    // Regra do ThorJava: nmunid no máximo 32 caracteres (colDescription.setMaxCharacters(32))
    if (form.nmunid.trim().length > 32) {
      toast(`${t('unidades.descriptionHint')} - max 32 chars`, 'error')
      return
    }
    
    // Regra do ThorJava: Casas Decimais min 0, max 4 (decimalColumnCasasDecimais.setMaxValue(4))
    const dec = Number(form.qtdecasasdec)
    if (isNaN(dec) || dec < 0 || dec > 4) {
      toast(`${t('unidades.casasDecimaisHint')} (0-4)`, 'error')
      return
    }
    
    if (!empresaId) { toast(t('unidades.noEmpresa'), 'error'); return }
    
    setSaving(true)
    const payload: UnidadeRow = {
      cdunid: form.cdunid.trim().toUpperCase().substring(0, 2),
      nmunid: form.nmunid.trim().substring(0, 32),
      status: form.status,
      cdunidnfe: form.cdunidnfe?.trim().toUpperCase() || null,
      qtdecasasdec: dec,
      cdunidpai: form.cdunidpai?.trim().toUpperCase() || null,
      fatorunidpai: Number(form.fatorunidpai),
    }

    try {
      if (editMode === 'new') {
        const created = await unidadesApi.create(empresaId, payload)
        setRows(r => [...r, created].sort((a, b) => a.cdunid.localeCompare(b.cdunid)))
        setSelected(created.cdunid)
        toast(t('unidades.createSuccess'))
      } else {
        const updated = await unidadesApi.update(empresaId, form.cdunid, payload)
        setRows(r => r.map(x => x.cdunid === form.cdunid ? updated : x))
        toast(t('unidades.updateSuccess'))
      }
      setEditMode('none')
    } catch (e: any) {
      toast(e?.response?.data?.detail || t('unidades.loadError'), 'error')
    } finally { setSaving(false) }
  }

  const handleInativar = async () => {
    if (!selected || !empresaId) return
    const row = rows.find(r => r.cdunid === selected)
    if (!row) return
    if (row.status === 'N') { toast(t('unidades.inativarAlready'), 'warning'); return }
    if (!window.confirm(t('unidades.inativarConfirm', { code: selected, description: row.nmunid }))) return
    try {
      const updated = await unidadesApi.update(empresaId, selected, { status: 'N' })
      setRows(r => r.map(x => x.cdunid === selected ? updated : x))
      toast(t('unidades.inativarSuccess'))
    } catch (e: any) {
      toast(e?.response?.data?.detail || t('unidades.loadError'), 'error')
    }
  }

  // Filtro dinâmico em tempo real (conforme UnidsGridFrame.java: Código/cdunid NÃO é filtrável)
  const filteredRows = rows.filter(row => {
    const q = search.toLowerCase().trim()
    if (!q) return true
    
    // Filtros permitidos: Descrição, NFE, Unidade Pai, Casas Decimais, Status
    const matchesDescription = row.nmunid.toLowerCase().includes(q)
    const matchesNfe = row.cdunidnfe ? row.cdunidnfe.toLowerCase().includes(q) : false
    const matchesPai = row.cdunidpai ? row.cdunidpai.toLowerCase().includes(q) : false
    const matchesDecimais = String(row.qtdecasasdec).includes(q)
    const matchesStatus = (row.status === 'S' ? t('unidades.statusActive').toLowerCase() : t('unidades.statusInactive').toLowerCase()).includes(q)
    
    return matchesDescription || matchesNfe || matchesPai || matchesDecimais || matchesStatus
  })

  // Classificação/Ordenação de colunas (Client-side)
  const [sortConfig, setSortConfig] = useState<{ key: keyof UnidadeRow; direction: 'asc' | 'desc' } | null>(null)

  const handleSort = (key: keyof UnidadeRow) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    } else if (sortConfig && sortConfig.key === key && sortConfig.direction === 'desc') {
      setSortConfig(null)
      return
    }
    setSortConfig({ key, direction })
  }

  const sortedRows = React.useMemo(() => {
    const items = [...filteredRows]
    if (sortConfig !== null) {
      items.sort((a, b) => {
        let valA = a[sortConfig.key]
        let valB = b[sortConfig.key]

        // Normalização de valores nulos/undefined
        if (valA === null || valA === undefined) valA = ''
        if (valB === null || valB === undefined) valB = ''

        // Comparação de tipos numéricos ou strings
        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortConfig.direction === 'asc' ? valA - valB : valB - valA
        }

        const strA = String(valA).toLowerCase()
        const strB = String(valB).toLowerCase()
        if (strA < strB) {
          return sortConfig.direction === 'asc' ? -1 : 1
        }
        if (strA > strB) {
          return sortConfig.direction === 'asc' ? 1 : -1
        }
        return 0
      })
    }
    return items
  }, [filteredRows, sortConfig])

  const renderSortIcon = (key: keyof UnidadeRow) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ArrowUpDown size={12} className="opacity-40 ml-1.5 group-hover:opacity-100 transition-opacity shrink-0" />
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={12} className="text-primary ml-1.5 shrink-0" />
      : <ArrowDown size={12} className="text-primary ml-1.5 shrink-0" />
  }

  // Exportar para Excel (xlsx via exportToExcel)
  const handleExportExcel = () => {
    const columns = [
      { key: 'cdunid', label: t('unidades.code') },
      { key: 'cdunidnfe', label: t('unidades.unidadeNfe') },
      { key: 'nmunid', label: t('unidades.description') },
      { key: 'qtdecasasdec', label: t('unidades.casasDecimais') },
      { key: 'status', label: t('unidades.status') },
      { key: 'cdunidpai', label: t('unidades.refUnit') },
      { key: 'fatorunidpai', label: t('unidades.fatorLabel') }
    ]
    const dataRows = filteredRows.map(row => ({
      cdunid: row.cdunid,
      cdunidnfe: row.cdunidnfe || '',
      nmunid: row.nmunid,
      qtdecasasdec: row.qtdecasasdec,
      status: row.status === 'S' ? t('unidades.statusActive') : t('unidades.statusInactive'),
      cdunidpai: row.cdunidpai || '',
      fatorunidpai: row.fatorunidpai !== null && row.fatorunidpai !== undefined ? Number(row.fatorunidpai).toFixed(8) : '0'
    }))
    
    exportToExcel(dataRows, columns, {
      filename: `unidades_medida_${new Date().toISOString().split('T')[0]}.xlsx`,
      sheetName: 'Unidades de Medida'
    })
    
    toast(t('unidades.exportExcelSuccess'))
  }

  const selectedRow = rows.find(r => r.cdunid === selected)

  return (
    <div className="flex flex-col h-full w-full bg-background text-foreground transition-colors duration-200 font-sans">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-border shrink-0 bg-card">
        <span className="text-primary shrink-0"><Scale size={18} /></span>
        <span className="font-semibold text-base">{t('unidades.title')}</span>
        <span className="text-muted-foreground text-sm ml-1">/ {t('menu.tabelasBasicas', 'Tabelas › Básicas')} › {t('unidades.title')}</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {loading ? t('common.loading') : `${filteredRows.length} / ${rows.length}`}
        </span>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 bg-muted/30 border-b border-border shrink-0 flex-wrap">
        <button 
          onClick={handleNew} 
          disabled={editMode !== 'none'}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded text-sm font-medium transition-colors cursor-pointer"
        >
          <Plus size={14} /> {t('common.create')}
        </button>
        <button 
          onClick={handleEdit} 
          disabled={!selected || editMode !== 'none'}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-40 rounded text-sm font-medium transition-colors cursor-pointer"
        >
          <Pencil size={14} /> {t('common.edit')}
        </button>
        {editMode !== 'none' && (
          <>
            <button 
              onClick={handleSave} 
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded text-sm font-medium transition-colors cursor-pointer"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {t('common.save')}
            </button>
            <button 
              onClick={handleCancel}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-500 hover:bg-slate-400 text-white rounded text-sm font-medium transition-colors cursor-pointer"
            >
              <X size={14} /> {t('common.cancel')}
            </button>
          </>
        )}
        <div className="h-4 w-px bg-border mx-1 shrink-0" />
        <button 
          onClick={handleInativar} 
          disabled={!selected || editMode !== 'none' || selectedRow?.status === 'N'}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-40 text-white rounded text-sm font-medium transition-colors cursor-pointer"
        >
          <XCircle size={14} /> {t('unidades.inativar')}
        </button>

        {/* Campo de Filtro em Tempo Real */}
        <div className="relative ml-2 min-w-[200px] max-w-xs flex-1 shrink-0">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input 
            type="text" 
            placeholder={t('common.search') + '...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 bg-card border border-border rounded text-xs focus:outline-none focus:border-primary text-foreground"
          />
        </div>

        {/* Botão de Exportar para Excel */}
        <button 
          onClick={handleExportExcel}
          disabled={filteredRows.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white rounded text-sm font-medium transition-colors cursor-pointer ml-1"
        >
          <Download size={14} /> {t('unidades.exportExcel')}
        </button>

        <button 
          onClick={load} 
          disabled={editMode !== 'none'}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-card hover:bg-muted border border-border text-foreground disabled:opacity-40 rounded text-sm font-medium transition-colors ml-auto cursor-pointer"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> {loading ? t('common.loading') : 'Reload'}
        </button>
      </div>

      {/* Main Workspace split panel */}
      <div className="flex flex-1 min-h-0 w-full overflow-hidden">
        {/* Table Area */}
        <div className="flex-1 min-w-0 overflow-y-auto border-r border-border bg-card">
          <table className="w-full border-collapse text-left text-sm text-foreground">
            <thead className="bg-muted/60 sticky top-0 border-b border-border text-muted-foreground font-medium z-10">
              <tr className="select-none">
                <th 
                  onClick={() => handleSort('cdunid')}
                  className="px-4 py-2.5 font-semibold cursor-pointer group hover:text-foreground transition-colors"
                >
                  <div className="flex items-center">
                    {t('unidades.code')} {renderSortIcon('cdunid')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('cdunidnfe')}
                  className="px-4 py-2.5 font-semibold cursor-pointer group hover:text-foreground transition-colors"
                >
                  <div className="flex items-center">
                    {t('unidades.unidadeNfe')} {renderSortIcon('cdunidnfe')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('nmunid')}
                  className="px-4 py-2.5 font-semibold cursor-pointer group hover:text-foreground transition-colors"
                >
                  <div className="flex items-center">
                    {t('unidades.description')} {renderSortIcon('nmunid')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('qtdecasasdec')}
                  className="px-4 py-2.5 font-semibold cursor-pointer group hover:text-foreground transition-colors text-right"
                >
                  <div className="flex items-center justify-end">
                    {t('unidades.casasDecimais')} {renderSortIcon('qtdecasasdec')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('status')}
                  className="px-4 py-2.5 font-semibold cursor-pointer group hover:text-foreground transition-colors text-center"
                >
                  <div className="flex items-center justify-center">
                    {t('unidades.status')} {renderSortIcon('status')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('cdunidpai')}
                  className="px-4 py-2.5 font-semibold cursor-pointer group hover:text-foreground transition-colors"
                >
                  <div className="flex items-center">
                    {t('unidades.refUnit')} {renderSortIcon('cdunidpai')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('fatorunidpai')}
                  className="px-4 py-2.5 font-semibold cursor-pointer group hover:text-foreground transition-colors text-right"
                >
                  <div className="flex items-center justify-end">
                    {t('unidades.fatorLabel')} {renderSortIcon('fatorunidpai')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sortedRows.map((row, i) => {
                const isSel = selected === row.cdunid
                return (
                  <tr 
                    key={row.cdunid}
                    onClick={() => { if (editMode === 'none') setSelected(isSel ? null : row.cdunid) }}
                    onDoubleClick={() => {
                      setSelected(row.cdunid)
                      setForm({
                        cdunid: row.cdunid,
                        nmunid: row.nmunid,
                        status: row.status,
                        cdunidnfe: row.cdunidnfe || '',
                        qtdecasasdec: row.qtdecasasdec,
                        cdunidpai: row.cdunidpai || '',
                        fatorunidpai: row.fatorunidpai
                      })
                      setEditMode('edit')
                    }}
                    className={`cursor-pointer transition-colors ${
                      isSel ? 'bg-primary/20 text-foreground font-semibold border-l-2 border-primary' 
                        : i % 2 === 0 ? 'bg-card hover:bg-muted/30' 
                        : 'bg-muted/10 hover:bg-muted/30'
                    } ${row.status === 'N' ? 'text-muted-foreground line-through' : ''}`}
                  >
                    <td className="px-4 py-2 font-mono">{row.cdunid}</td>
                    <td className="px-4 py-2 font-mono">{row.cdunidnfe || '-'}</td>
                    <td className="px-4 py-2 font-medium">{row.nmunid}</td>
                    <td className="px-4 py-2 text-right">{row.qtdecasasdec}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded text-xxs font-semibold uppercase ${
                        row.status === 'S' 
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' 
                          : 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400'
                      }`}>
                        {row.status === 'S' ? t('common.active') : t('common.inactive')}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono">{row.cdunidpai || '-'}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      {row.fatorunidpai !== null && row.fatorunidpai !== undefined 
                        ? Number(row.fatorunidpai).toFixed(8) 
                        : '-'}
                    </td>
                  </tr>
                )
              })}
              {filteredRows.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-muted-foreground">
                    {search ? t('common.noResults') : t('common.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Form Panel (Edit / Create) */}
        {editMode !== 'none' && (
          <div className="w-[380px] border-l border-border bg-muted/20 overflow-y-auto shrink-0 p-5 flex flex-col gap-4">
            <div className="flex items-center gap-1.5 pb-2 border-b border-border">
              <span className="text-primary"><Scale size={16} /></span>
              <span className="font-semibold text-sm">
                {editMode === 'new' ? t('unidades.newTitle') : t('unidades.editTitle', { code: form.cdunid })}
              </span>
            </div>

            <div className="flex flex-col gap-3">
              {/* Código */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  {t('unidades.codeHint')}
                </label>
                <input
                  type="text"
                  value={form.cdunid}
                  onChange={(e) => setForm(f => ({ ...f, cdunid: e.target.value }))}
                  disabled={editMode === 'edit'}
                  maxLength={2}
                  placeholder={t('unidades.codePlaceholder')}
                  className="w-full px-3 py-1.5 border border-border bg-card rounded text-sm text-foreground uppercase font-mono focus:border-primary focus:outline-none transition-colors disabled:bg-muted"
                />
              </div>

              {/* Unidade NFE */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  {t('unidades.unidadeNfeHint')}
                </label>
                <input
                  type="text"
                  value={form.cdunidnfe || ''}
                  onChange={(e) => setForm(f => ({ ...f, cdunidnfe: e.target.value }))}
                  maxLength={6}
                  placeholder={t('unidades.unidadeNfePlaceholder')}
                  className="w-full px-3 py-1.5 border border-border bg-card rounded text-sm text-foreground uppercase font-mono focus:border-primary focus:outline-none transition-colors"
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  {t('unidades.descriptionHint')}
                </label>
                <input
                  type="text"
                  value={form.nmunid}
                  onChange={(e) => setForm(f => ({ ...f, nmunid: e.target.value }))}
                  maxLength={32}
                  placeholder={t('unidades.descriptionPlaceholder')}
                  className="w-full px-3 py-1.5 border border-border bg-card rounded text-sm text-foreground focus:border-primary focus:outline-none transition-colors"
                />
              </div>

              {/* Casas Decimais */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  {t('unidades.casasDecimaisHint')}
                </label>
                <input
                  type="number"
                  min={0}
                  max={4}
                  value={form.qtdecasasdec}
                  onChange={(e) => setForm(f => ({ ...f, qtdecasasdec: parseInt(e.target.value) || 0 }))}
                  placeholder="0, 2, 3..."
                  className="w-full px-3 py-1.5 border border-border bg-card rounded text-sm text-foreground focus:border-primary focus:outline-none transition-colors"
                />
              </div>

              {/* Unidade Referência (Pai) */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  {t('unidades.refUnitHint')}
                </label>
                <select
                  value={form.cdunidpai || ''}
                  onChange={(e) => setForm(f => ({ ...f, cdunidpai: e.target.value || null }))}
                  className="w-full px-3 py-1.5 border border-border bg-card rounded text-sm text-foreground focus:border-primary focus:outline-none transition-colors"
                >
                  <option value="">{t('unidades.refUnitPlaceholder')}</option>
                  {rows.filter(r => r.cdunid !== form.cdunid).map(r => (
                    <option key={r.cdunid} value={r.cdunid}>
                      {r.cdunid} - {r.nmunid}
                    </option>
                  ))}
                </select>
              </div>

              {/* Relação Tabela Referência (Fator) */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  {t('unidades.fatorHint')}
                </label>
                <input
                  type="number"
                  step="any"
                  value={form.fatorunidpai}
                  onChange={(e) => setForm(f => ({ ...f, fatorunidpai: parseFloat(e.target.value) || 0 }))}
                  placeholder={t('unidades.fatorPlaceholder')}
                  className="w-full px-3 py-1.5 border border-border bg-card rounded text-sm text-foreground focus:border-primary focus:outline-none transition-colors"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  {t('common.status')}
                </label>
                <select
                  value={form.status}
                  onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full px-3 py-1.5 border border-border bg-card rounded text-sm text-foreground focus:border-primary focus:outline-none transition-colors"
                >
                  <option value="S">{t('common.active')}</option>
                  <option value="N">{t('common.inactive')}</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Floating Toasts container */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <div 
            key={t.id}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm border font-medium pointer-events-auto text-white ${
              t.type === 'success' ? 'bg-emerald-600 border-emerald-500' :
              t.type === 'error' ? 'bg-rose-600 border-rose-500' :
              'bg-amber-600 border-amber-500'
            }`}
          >
            {t.type === 'success' && <Check size={16} />}
            {t.type === 'error' && <XCircle size={16} />}
            {t.type === 'warning' && <AlertTriangle size={16} />}
            <span>{t.msg}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default UnidadesPage
